import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useStore } from '../store/useStore'
import { Capacitor } from '@capacitor/core'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, metadata?: { nickname?: string; referral_code?: string }, emailRedirectTo?: string) => Promise<{ data: any }>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    let sessionCheckTimeout: NodeJS.Timeout

    // 初回セッションチェック（タイムアウト付き）
    const initSession = async () => {
      try {
        // タイムアウトを5秒に設定
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) => {
          sessionCheckTimeout = setTimeout(() => reject(new Error('Session check timeout')), 5000)
        })

        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any

        if (mounted) {
          if (error) {
            console.error('Session check error:', error)
            setSession(null)
            setUser(null)
          } else {
            setSession(session)
            setUser(session?.user ?? null)
          }
          setLoading(false)
        }
      } catch (err) {
        console.error('Session initialization error:', err)
        if (mounted) {
          setSession(null)
          setUser(null)
          setLoading(false)
        }
      }
    }

    initSession()

    // セッション変更の監視
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    })

    // バックグラウンドから戻った時のセッション再確認
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && mounted) {
        try {
          // セッションを再取得（タイムアウトなし、既存のセッションを使用）
          const { data: { session } } = await supabase.auth.getSession()
          if (mounted && session) {
            setSession(session)
            setUser(session.user)
          }
        } catch (err) {
          console.error('Session refresh on visibility change error:', err)
          // エラーが発生してもユーザーを保持（ネットワーク問題の可能性）
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // ネイティブアプリでGoogle Auth SDKを初期化
    // serverClientId は capacitor.config.ts で設定済み（Supabase が受け付ける Web Client ID）
    if (Capacitor.isNativePlatform()) {
      GoogleAuth.initialize({
        clientId: '620820187286-kum14pu3bj6ak4ad116nem6rptm122r5.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true
      })
    }

    return () => {
      mounted = false
      clearTimeout(sessionCheckTimeout)
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // ユーザーアクティビティを更新（5分間隔でスロットリング）
  useEffect(() => {
    if (!user) return

    let lastUpdate = 0
    const UPDATE_INTERVAL = 5 * 60 * 1000 // 5分

    const updateActivity = async () => {
      const now = Date.now()
      if (now - lastUpdate < UPDATE_INTERVAL) return
      lastUpdate = now

      try {
        await supabase.rpc('update_user_last_active')
      } catch (err) {
        // エラーは無視（ユーザー体験に影響しない）
        console.debug('Activity update failed:', err)
      }
    }

    // 初回更新
    updateActivity()

    // ユーザー操作時に更新
    const handleInteraction = () => updateActivity()
    window.addEventListener('click', handleInteraction)
    window.addEventListener('keydown', handleInteraction)
    window.addEventListener('touchstart', handleInteraction)

    // 定期更新（アクティブなタブの場合のみ）
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        updateActivity()
      }
    }, UPDATE_INTERVAL)

    return () => {
      window.removeEventListener('click', handleInteraction)
      window.removeEventListener('keydown', handleInteraction)
      window.removeEventListener('touchstart', handleInteraction)
      clearInterval(intervalId)
    }
  }, [user])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error

    // ログイン統計を更新
    try {
      await supabase.rpc('update_user_login_stats')
    } catch (err) {
      console.debug('Login stats update failed:', err)
    }
  }

  const signUp = async (
    email: string,
    password: string,
    metadata?: { nickname?: string; referral_code?: string },
    emailRedirectTo?: string
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: emailRedirectTo || import.meta.env.VITE_APP_URL || window.location.origin,
        data: metadata || {}
      }
    })
    if (error) throw error
    return { data }
  }

  const signInWithGoogle = async () => {
    if (Capacitor.isNativePlatform()) {
      // ネイティブアプリ: Google Sign-In SDKを使用
      try {
        // Google Sign-In SDKでサインイン（ネイティブUIを表示）
        const googleUser = await GoogleAuth.signIn()

        // idTokenを取得
        const idToken = googleUser.authentication.idToken
        if (!idToken) {
          throw new Error('No ID token received from Google')
        }

        // SupabaseにidTokenを送信してセッションを作成
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken
        })

        if (error) {
          throw error
        }

        if (data.session) {
          setSession(data.session)
          setUser(data.session.user)

          // ログイン統計を更新
          try {
            await supabase.rpc('update_user_login_stats')
          } catch (err) {
            console.debug('Login stats update failed:', err)
          }
        }
      } catch (err: any) {
        // ユーザーがキャンセルした場合は静かに終了
        if (err.error === 'popup_closed_by_user' || err.message?.includes('canceled')) {
          return
        }
        throw err
      }
    } else {
      // Web: 通常のOAuth認証
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: import.meta.env.VITE_APP_URL || window.location.origin
        }
      })
      if (error) throw error
    }
  }

  const signOut = async () => {
    try {
      // Zustandストアをクリア
      useStore.getState().clearStore()

      // ネイティブアプリの場合、Google Sign-In SDKからもサインアウト
      // これにより次回Googleログイン時にアカウント選択画面が表示される
      if (Capacitor.isNativePlatform()) {
        try {
          await GoogleAuth.signOut()
        } catch {
          // サインアウト失敗は無視（ユーザー体験に影響しない）
        }
      }

      // サインアウト実行（これによりonAuthStateChangeが発火し、userがnullになる）
      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('Sign out error:', error)
      }

      // ローカルの状態をクリア（onAuthStateChangeでも設定されるが、念のため）
      setUser(null)
      setSession(null)

      // セッションストレージのみクリア（localStorageはSupabaseが管理）
      sessionStorage.clear()

      // PrivateRouteがuserがnullになったことを検知して自動的に/loginにリダイレクトする
      // window.location.hrefは使用しない（ネイティブアプリでページリロードを避けるため）
    } catch (error: any) {
      console.error('Sign out error:', error)
      // エラー時も状態をクリア
      setUser(null)
      setSession(null)
    }
  }

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}